import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  animation: ${fadeIn} 0.3s ease-out;
  display: ${(props) => (props.open ? 'block' : 'none')};
`;

const Sheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 24px 24px 0 0;
  z-index: 9999;
  max-height: 80vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
  display: ${(props) => (props.open ? 'block' : 'none')};
  transform: translateY(${(props) => props.translateY || 0}px);
  transition: ${(props) =>
    props.isDragging ? 'none' : 'transform 0.3s ease-out'};
`;

const HandleBar = styled.div`
  padding: 12px 20px 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const Handle = styled.div`
  width: 40px;
  height: 4px;
  background: #d1d5db;
  border-radius: 2px;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  top: 12px;
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Header = styled.div`
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const Title = styled.h3`
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const Content = styled.div`
  padding: 20px;
`;

const BottomSheet = ({ open, onClose, title, children }) => {
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setTranslateY(0);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    // Ne permettre que le drag vers le bas
    if (diff > 0) {
      setTranslateY(diff);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);

    // Si on a drag plus de 100px, on ferme
    if (translateY > 100) {
      onClose();
    }

    // Sinon on revient à la position initiale
    setTranslateY(0);
  };

  if (!open) return null;

  return (
    <>
      <Overlay open={open} onClick={onClose} />
      <Sheet open={open} translateY={translateY} isDragging={isDragging}>
        <HandleBar
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}>
          <Handle />
          <CloseButton onClick={onClose} aria-label="Fermer">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </CloseButton>
        </HandleBar>
        {title && (
          <Header>
            <Title>{title}</Title>
          </Header>
        )}
        <Content>{children}</Content>
      </Sheet>
    </>
  );
};

BottomSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default BottomSheet;
